import type { GetRequestByIdResponse } from "@nlc/shared-types";

import { RequestTypeEnum } from "@nlc/shared-types";
import { queryOptions, useQuery } from "@tanstack/react-query";

// eslint-disable-next-line import/no-restricted-paths
import { MOCKED_REQUEST } from "@/features/templates/mocks/requestDetails.ts";
import { requestKeys } from "@/hooks/queryKeys.ts";
import { useIsTemplate } from "@/hooks/useIsTemplate.tsx";
import { useRequestId } from "@/hooks/useRequestId.tsx";
import apiClient, { HttpError } from "@/lib/api/client";

async function fetchRequestById(
  requestId: number,
): Promise<GetRequestByIdResponse> {
  return apiClient
    .url(`/requests/${requestId}`)
    .get()
    .notFound(() => {
      throw new HttpError("Request not found", 404);
    })
    .json();
}

export function isRequestNotFoundError(error: unknown): boolean {
  return error instanceof HttpError && error.status === 404;
}

function requestByIdQueryOptions(requestId: number) {
  return queryOptions<GetRequestByIdResponse>({
    queryFn: () => fetchRequestById(requestId),
    queryKey: requestKeys.requestById(requestId),
    retry: (failureCount, error) =>
      !isRequestNotFoundError(error) && failureCount < 3,
  });
}

export function useGetRequest(fallbackRequestId?: number) {
  const requestIdFromUrl = useRequestId();
  const finalId = requestIdFromUrl ?? fallbackRequestId;

  const { isTemplate, requestType } = useIsTemplate();

  return useQuery<GetRequestByIdResponse>({
    ...requestByIdQueryOptions(Number(finalId)),
    enabled: !!finalId || isTemplate,
    queryFn: async () => {
      if (requestType == RequestTypeEnum.GRAPHIC) return MOCKED_REQUEST.graphic;
      if (requestType === RequestTypeEnum.VIDEO) return MOCKED_REQUEST.video;

      return fetchRequestById(Number(finalId));
    },
    select: (data) => {
      if (requestType == RequestTypeEnum.GRAPHIC) {
        return MOCKED_REQUEST.graphic;
      }
      if (requestType === RequestTypeEnum.VIDEO) return MOCKED_REQUEST.video;

      return {
        ...data,
      };
    },
  });
}
